import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscArea,
  BscCiclo,
  BscCicloTrimestre,
  BscEjercicio,
  BscEjercicioKpi,
  BscEjercicioTrimestre,
  BscHistorial,
  BscHito,
  BscKpiMes,
  BscKpiTrimestre,
  BscRes,
  EstadoMesBsc,
  ViaCapturaBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { ACCION_HISTORIAL, ESTADO_MES, ESTATUS_PLANEACION, EstadoBsc } from "../bsc-estados";
import {
  SIN_DATO,
  diaMes,
  fechaHora,
  mesEnTexto,
  mesesDeTrimestre,
  nombreMes,
  numero,
  pct,
  trimestreDeMes,
} from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn } from "../bsc-kpi";
import { CapturaMesBscComponent, ResultadoCapturaMesBsc, abrirCapturaMesBsc } from "../kpi/captura-mes-bsc.component";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";

/**
 * Qué enseña la pantalla para el año y el área:
 *   CARGANDO       la consulta en vuelo (la primera vez, o al cambiar de año o área);
 *   ERROR          miBsc.do falló (sin la hoja, sin sesión, sin conexión);
 *   SIN_AREAS      no estoy asignado a ningún área (y no reviso ni administro);
 *   SIN_CICLO      el año no está dado de alta (Calidad lo abre en Parámetros);
 *   SIN_PLANEACION el área no tiene planeación en el año;
 *   SIN_AUTORIZAR  la planeación existe pero Dirección no la ha autorizado: no hay trimestres;
 *   DATOS          el ciclo del año, el checklist, las fechas y la bitácora.
 */
type EstadoMiBsc = "CARGANDO" | "ERROR" | "SIN_AREAS" | "SIN_CICLO" | "SIN_PLANEACION" | "SIN_AUTORIZAR" | "DATOS";

/** Un área del combo: las mías y, si reviso o administro, las demás (miBsc.do las da). */
interface OpcionArea {
  idBscArea: number;
  nombre: string;
  via: ViaCapturaBsc;
}

/** Un trozo de la frase «Te toca»: lo fuerte va en <strong>, como la maqueta. */
interface Segmento {
  texto: string;
  fuerte?: boolean;
}

/** Un paso del ciclo del año: la planeación o un trimestre. */
interface PasoCiclo {
  /** null = la planeación. */
  q: number | null;
  numero: number;
  titulo: string;
  /** "Autorizado · 24% · 9 abr", "2 de 3 meses · límite 15 oct"… */
  detalle: string;
  /** is-hecho | is-enviado | is-actual | is-alerta | is-espera */
  clase: string;
  /** El estado en palabras para el lector de pantalla del botón. */
  estadoTexto: string;
}

/** Un mes del trimestre elegido, en el selector de mes del checklist. */
interface OpcionMes {
  mes: number;
  nombre: string;
  capturados: number;
  vigentes: number;
  /** is-completo | is-vencido | is-cerrado | "" */
  clase: string;
}

/** Un KPI en el checklist del mes elegido, armado UNA vez por respuesta o por cambio de mes. */
interface RenglonKpi {
  idEjercicioKpi: number;
  objetivo: string;
  estado: EstadoBsc<EstadoMesBsc> | null;
  capturado: boolean;
  /** Aplica en el trimestre (los NA se listan pero no cuentan). */
  vigente: boolean;
  /** "Capturado 26 sep por Ana López", "Aplica desde Q4 · solo registra actividad"… */
  detalle: string;
  /** "Avance Q3: 90% · acumulado 47.5% de 50%" o "Avance Q3 sin capturar". */
  avance: string;
  capturable: boolean;
}

/** Una fecha del trimestre elegido. */
interface FechaQ {
  clave: string;
  texto: string;
  fecha: string;
  /** "Cumplido", "Vencido", "Próximo", "En espera" o vacío. */
  estado: string;
  /** is-ok | is-vencido | is-proximo | "" */
  clase: string;
  icono: string;
}

/** Un renglón de la bitácora del área. */
interface Movimiento {
  clave: string;
  cuando: string;
  accion: string;
  quien: string;
  detalle: string;
}

/** El mes de «Te toca» (nulo si no hay), cuántos KPIs le faltan y cuántos meses más ya vencieron. */
interface MesPendiente {
  mes: number | null;
  faltan: number;
  masVencidos: number;
}

/** El pie del checklist: el cierre del trimestre elegido. */
interface CierreQ {
  /** Hay botón (EN_CAPTURA o REGRESADO). */
  boton: boolean;
  habilitado: boolean;
  textoBoton: string;
  texto: string;
}

const TRIMESTRES = [1, 2, 3, 4];
/** Lo que se ve de la bitácora antes de «Ver toda». */
const BITACORA_CORTA = 8;

/** Las acciones de la bitácora cuyo comentario lo escribe el sistema, no una persona. */
const COMENTARIO_DEL_SISTEMA = new Set(["CAPTURA_MES", "CAPTURA_REAL"]);

const RUTA = {
  tablero: "/admin/bsc/tablero",
  area: "/admin/bsc/tablero/area",
  kpi: "/admin/bsc/tablero/kpi",
  planeacion: "/admin/bsc/planeacion",
  cargaExcel: "/admin/bsc/carga-excel",
  parametros: "/admin/bsc/parametros",
  cierre: "/admin/bsc/mi-bsc/cierre",
};

/**
 * BSC · C1 Mi BSC del responsable de área (docs/BSC.md §4, §7 y Anexo B,
 * maqueta «C1-MiBSC»; F6.4).
 *
 * Lo que me toca del año en un área: «Te toca» (el primer mes ya empezado con
 * KPIs sin capturar y el trimestre que se cierra, con su fecha límite), el
 * ciclo del año (planeación y Q1..Q4 con estatus, alcance y fechas; cada
 * trimestre es una pestaña), el checklist de un mes del trimestre elegido
 * (capturado, pendiente, vencido, cerrado o no aplica por KPI, con Capturar o
 * Editar en el cuadro captura-mes-bsc y Ver en la ficha A3), las fechas del
 * trimestre (de los hitos de F6.3; «sin fecha límite» cuando T4 no la fijó),
 * la bitácora del área y «Cerrar Qn (faltan N)», que lleva a C3 cuando los
 * meses están completos y el trimestre anterior autorizado.
 *
 * Todo sale de miBsc.do (BscCapturaBuss.miBsc): las áreas en que capturo (con
 * el combo si son varias), las planeaciones del año, y de la elegida los
 * trimestres decorados, las fechas del ciclo, los KPI activos con K/L/N, sus
 * capturas, sus 12 meses con estado (la regla de A3), los hitos y la
 * bitácora. Lo que dice la pantalla es lectura: quién captura, la vía, el año
 * y el estatus del trimestre lo vuelven a exigir mes.do y real.do.
 *
 * Se captura si: puedeCapturar (hoja mi-bsc y el área en mis manos), la vía
 * del área no es solo EXCEL, la planeación está AUTORIZADA y no viene de un
 * FOR-011 histórico, el año está ABIERTO y el trimestre del mes no está
 * ENVIADO ni AUTORIZADO. Un PENDIENTE se captura (julio con Q2 en revisión).
 *
 * Base visual: table-page (hero, barra del selector con el área proyectada
 * como T3, tarjetas) con el ciclo como pestañas role="tablist" (patrón de
 * vacaciones/seguimiento-vacaciones). Año y área viajan en la URL
 * (?anio=&idBscArea=).
 */
@Component({
  selector: "app-mi-bsc",
  standalone: true,
  templateUrl: "./mi-bsc.component.html",
  // dashboard-tarjetas.css para el área y el resumen que se proyectan en la
  // barra del selector: llevan el _ngcontent de esta pantalla (como T3).
  styleUrls: ["../../dashboard/shared/dashboard-tarjetas.css", "../bsc.shared.css", "./mi-bsc.component.css"],
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule, SelectorCorteBscComponent],
})
export class MiBscComponent implements OnInit, OnDestroy {
  @ViewChild(SelectorCorteBscComponent) selector?: SelectorCorteBscComponent;

  readonly ruta = RUTA;
  readonly trimestres = TRIMESTRES;

  anioInicial: number | null = null;
  anio: number | null = null;
  idBscArea: number | null = null;

  estado: EstadoMiBsc = "CARGANDO";
  loading = false;
  error = "";
  /** El área del enlace no es de las mías: se dice cuál se abrió. */
  avisoArea = "";
  /** Lo último que se guardó en el cuadro de captura (región role="status"). */
  mensajeGuardado = "";

  opcionesArea: OpcionArea[] = [];
  ciclo: BscCiclo | null = null;
  ejercicio: BscEjercicio | null = null;
  kpis: BscEjercicioKpi[] = [];
  avisos: string[] = [];

  puedeCapturar = false;
  puedeCerrar = false;
  puedeCargarExcel = false;
  puedeConfigurar = false;
  verTablero = false;
  verPlaneacion = false;

  /* ---------- Lo que se pinta, armado una vez por respuesta ---------- */
  teToca: Segmento[] = [];
  /** El mes de «Te toca» (el botón del hero lo captura); nulo si no hay. */
  mesTeToca: number | null = null;
  pasoPlaneacion: PasoCiclo | null = null;
  pasos: PasoCiclo[] = [];
  qElegido = 1;
  mesElegido = 1;
  opcionesMes: OpcionMes[] = [];
  renglones: RenglonKpi[] = [];
  /** "Captura de septiembre · 5 de 8 KPIs". */
  tituloMes = "";
  /** 0..100 para la barra. */
  avanceMes = 0;
  limiteMes = "";
  /** La fecha límite del mes elegido ("5 oct"); vacía sin día límite. */
  private fechaLimiteMes = "";
  /** Por qué el mes no se captura (trimestre cerrado, vía, histórico…); vacío si se captura. */
  notaMes = "";
  regreso = "";
  cierre: CierreQ | null = null;
  fechas: FechaQ[] = [];
  notaFechas = "";
  movimientos: Movimiento[] = [];
  verTodaBitacora = false;

  /* ---------- Índices de la respuesta ---------- */
  private trimestrePorQ = new Map<number, BscEjercicioTrimestre>();
  private fechasPorQ = new Map<number, BscCicloTrimestre>();
  private mesesPorKpi = new Map<number, Map<number, BscKpiMes>>();
  private capturasPorKpi = new Map<number, Map<number, BscKpiTrimestre>>();
  private kpiPorId = new Map<number, BscEjercicioKpi>();
  private hitos: BscHito[] = [];

  private idAreaPedida: number | null = null;
  private primeraConsulta = true;
  private consulta?: Subscription;
  private query$?: Subscription;
  private dialogo?: MatDialogRef<CapturaMesBscComponent, ResultadoCapturaMesBsc>;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.anioInicial = anioValido(params.get("anio"));
    this.idAreaPedida = idValido(params.get("idBscArea"));
    this.idBscArea = this.idAreaPedida;
    const menus = this.authService.getMenuList();
    this.verTablero = tieneAccesoMenu(menus, RUTA.tablero);
    this.verPlaneacion = tieneAccesoMenu(menus, RUTA.planeacion);
    // Angular reutiliza la pantalla si solo cambian los parámetros (el
    // historial del navegador): se abre lo que pide. Las navegaciones propias
    // (replaceUrl) traen lo mismo y no hacen nada.
    this.query$ = this.route.queryParamMap.subscribe((q) => {
      const anio = anioValido(q.get("anio"));
      const area = idValido(q.get("idBscArea"));
      const otroAnio = anio != null && this.anio != null && anio !== this.anio;
      const otraArea = area != null && this.idBscArea != null && area !== this.idBscArea;
      if ((otroAnio || otraArea) && !this.primeraConsulta) {
        if (otroAnio && anio != null) {
          this.anio = anio;
          // El combo solo lee [anio] al abrir: se mueve a mano, como en C2.
          this.selector?.fijarAnio(anio);
        }
        if (otraArea) {
          this.idBscArea = area;
        }
        this.consultar();
      }
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.query$?.unsubscribe();
    // El cuadro vive en el overlay y sobrevive al cambio de ruta.
    this.dialogo?.close();
  }

  /* ============================ Año y área ============================ */

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    if (seleccion.anio === this.anio && !this.primeraConsulta) {
      return;
    }
    this.anio = seleccion.anio;
    this.consultar();
  }

  cambiaArea(evento: Event): void {
    const nueva = idValido((evento.target as HTMLSelectElement).value);
    if (nueva == null || nueva === this.idBscArea) {
      return;
    }
    this.idBscArea = nueva;
    this.avisoArea = "";
    this.mensajeGuardado = "";
    this.consultar();
  }

  /* ============================ Consulta ============================ */

  /**
   * @param silencioso tras guardar en el cuadro: la pantalla se queda como
   *        está (mismo trimestre y mes) mientras contesta.
   * @param despues lo que sigue con la respuesta ya pintada (el siguiente KPI).
   */
  consultar(silencioso = false, despues?: () => void): void {
    if (this.anio == null) {
      return;
    }
    const anio = this.anio;
    const idBscArea = this.idBscArea;
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    if (!silencioso) {
      this.estado = "CARGANDO";
    }
    this.cdr.detectChanges();

    this.consulta = this.bscService.miBsc(anio, idBscArea).subscribe({
      next: (res) => {
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.loading = false;
        if (!res || res.codigoError) {
          // El área del enlace no es de las mías (exigirArea): se abre la que elija el servidor.
          if (res?.codigoError === "00002" && this.primeraConsulta && idBscArea != null) {
            this.idBscArea = null;
            this.avisoArea = "El área del enlace no está entre las que capturas: se abrió la tuya.";
            this.consultar();
            return;
          }
          this.falla(this.mensaje(res, "No se pudo consultar Mi BSC."));
          return;
        }
        this.primeraConsulta = false;
        this.aplica(res, silencioso);
        this.cdr.detectChanges();
        despues?.();
      },
      error: () => {
        if (anio !== this.anio || idBscArea !== this.idBscArea) {
          return;
        }
        this.loading = false;
        this.falla("No se pudo consultar Mi BSC: sin conexión con el servidor.");
      },
    });
  }

  private falla(texto: string): void {
    this.primeraConsulta = false;
    this.error = texto;
    this.estado = "ERROR";
    this.limpia();
    this.cdr.detectChanges();
  }

  private limpia(): void {
    this.ciclo = null;
    this.ejercicio = null;
    this.kpis = [];
    this.avisos = [];
    this.teToca = [];
    this.mesTeToca = null;
    this.pasoPlaneacion = null;
    this.pasos = [];
    this.renglones = [];
    this.opcionesMes = [];
    this.fechas = [];
    this.movimientos = [];
    this.cierre = null;
    this.hitos = [];
    this.trimestrePorQ = new Map();
    this.fechasPorQ = new Map();
    this.mesesPorKpi = new Map();
    this.capturasPorKpi = new Map();
    this.kpiPorId = new Map();
  }

  private aplica(res: BscRes, silencioso: boolean): void {
    this.avisos = res.avisos ?? [];
    this.puedeCapturar = res.puedeCapturar === true;
    this.puedeCerrar = res.puedeCerrar === true;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.opcionesArea = [...(res.areas ?? [])]
      .filter((a) => a.idBscArea != null)
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0) || (a.nombre ?? "").localeCompare(b.nombre ?? ""))
      .map((a: BscArea) => ({
        idBscArea: a.idBscArea as number,
        nombre: a.nombre || `Área ${a.idBscArea}`,
        via: a.viaCaptura ?? "AMBAS",
      }));
    this.ciclo = res.ciclo ?? null;
    this.ejercicio = res.ejercicio ?? null;
    this.kpis = (res.kpis ?? []).filter((k) => k.idEjercicioKpi != null && k.estatus !== "RETIRADO");
    this.hitos = res.hitos ?? [];

    // El área que quedó: la de la planeación elegida, la pedida o la primera del combo.
    const pedida = this.opcionesArea.find((a) => a.idBscArea === this.idBscArea);
    this.idBscArea = this.ejercicio?.idBscArea ?? pedida?.idBscArea ?? this.opcionesArea[0]?.idBscArea ?? null;
    if (this.anio != null && this.idBscArea != null) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { anio: this.anio, idBscArea: this.idBscArea },
        replaceUrl: true,
      });
    }

    this.indexa(res);
    this.movimientos = (res.historial ?? []).slice().reverse().map((h, i) => movimiento(h, i));

    if (this.opcionesArea.length === 0 && !this.ejercicio) {
      this.estado = "SIN_AREAS";
    } else if (!this.ciclo) {
      this.estado = "SIN_CICLO";
    } else if (!this.ejercicio) {
      this.estado = "SIN_PLANEACION";
    } else if (this.ejercicio.estatus !== "AUTORIZADA") {
      this.estado = "SIN_AUTORIZAR";
    } else {
      this.estado = "DATOS";
    }

    this.pasoPlaneacion = this.ejercicio ? this.pasoDePlaneacion(this.ejercicio) : null;
    this.pasos = this.estado === "DATOS" ? TRIMESTRES.map((q) => this.pasoDe(q)) : [];
    this.armaTeToca();
    if (this.estado === "DATOS") {
      if (!silencioso || !this.trimestrePorQ.has(this.qElegido)) {
        this.qElegido = this.qInicial();
        this.mesElegido = this.mesInicial(this.qElegido);
      }
      this.armaTrimestre();
    }
  }

  private indexa(res: BscRes): void {
    this.trimestrePorQ = new Map();
    for (const t of res.trimestres ?? []) {
      if (t.trimestre != null) {
        this.trimestrePorQ.set(t.trimestre, t);
      }
    }
    this.fechasPorQ = new Map();
    for (const f of res.trimestresCiclo ?? []) {
      if (f.trimestre != null) {
        this.fechasPorQ.set(f.trimestre, f);
      }
    }
    this.kpiPorId = new Map(this.kpis.map((k) => [k.idEjercicioKpi as number, k]));
    this.mesesPorKpi = new Map();
    for (const m of res.meses ?? []) {
      if (m.idEjercicioKpi == null || m.mes == null) {
        continue;
      }
      if (!this.mesesPorKpi.has(m.idEjercicioKpi)) {
        this.mesesPorKpi.set(m.idEjercicioKpi, new Map());
      }
      this.mesesPorKpi.get(m.idEjercicioKpi)?.set(m.mes, m);
    }
    this.capturasPorKpi = indiceDeCapturas(res.capturas ?? []);
  }

  /* ============================ Reglas de la pantalla ============================ */

  get area(): OpcionArea | null {
    return this.opcionesArea.find((a) => a.idBscArea === this.idBscArea) ?? null;
  }

  get nombreArea(): string {
    return this.ejercicio?.area || this.area?.nombre || "";
  }

  get titulo(): string {
    const area = this.nombreArea;
    if (!area) {
      return "Mi BSC";
    }
    return `Mi BSC · ${area}${this.anio != null ? " " + this.anio : ""}`;
  }

  get historica(): boolean {
    return this.ejercicio?.origen === "IMPORTACION_HISTORICA";
  }

  get cicloCerrado(): boolean {
    return this.ciclo?.estatus === "CERRADO";
  }

  get viaExcel(): boolean {
    return this.area?.via === "EXCEL";
  }

  /** Se captura en esta área y este año (lo de cada trimestre aparte: abiertoACaptura). */
  get capturaHabilitada(): boolean {
    return this.estado === "DATOS" && this.puedeCapturar && !this.viaExcel && !this.historica && !this.cicloCerrado;
  }

  /** "8 KPIs · vía guiada". */
  get resumenBarra(): string {
    const n = this.kpis.length;
    return `${n} ${n === 1 ? "KPI" : "KPIs"} en la planeación`;
  }

  get textoVia(): string {
    switch (this.area?.via) {
      case "EXCEL":
        return "Captura con el FOR-011";
      case "GUIADO":
        return "Captura en el flujo guiado";
      default:
        return "Flujo guiado o FOR-011";
    }
  }

  get queryArea(): Record<string, string | number | null> {
    return { anio: this.anio, corte: `${this.qElegido}` };
  }

  get queryPlaneacion(): Record<string, number | null> {
    return { anio: this.anio, idBscArea: this.idBscArea };
  }

  get bitacoraVisible(): Movimiento[] {
    return this.verTodaBitacora ? this.movimientos : this.movimientos.slice(0, BITACORA_CORTA);
  }

  /** El trimestre recibe captura: tiene renglón, no está ENVIADO ni AUTORIZADO y la planeación no es histórica. */
  private abiertoACaptura(q: number): boolean {
    const t = this.trimestrePorQ.get(q);
    return !!t && t.estatus !== "ENVIADO" && t.estatus !== "AUTORIZADO" && !this.historica;
  }

  /** Los KPI que cuentan en el trimestre (ACTIVO y que aplican: la regla del cierre). */
  private vigentes(q: number): BscEjercicioKpi[] {
    return this.kpis.filter((k) => aplicaEn(k, q));
  }

  /** Un renglón de bsc_kpi_mes cuenta como capturado (también un "NA" importado), como en el cierre. */
  private capturado(idEjercicioKpi: number, mes: number): boolean {
    return this.mesesPorKpi.get(idEjercicioKpi)?.get(mes)?.id != null;
  }

  /** El mes ya empezó (hoy en el navegador): «Te toca» no pide meses futuros. */
  private iniciado(mes: number): boolean {
    const hoy = new Date();
    const anio = this.anio ?? hoy.getFullYear();
    return anio < hoy.getFullYear() || (anio === hoy.getFullYear() && mes <= hoy.getMonth() + 1);
  }

  private faltanEnMes(mes: number): BscEjercicioKpi[] {
    return this.vigentes(trimestreDeMes(mes)).filter((k) => !this.capturado(k.idEjercicioKpi as number, mes));
  }

  /** El mes ya venció para alguno de sus pendientes (estado VENCIDO de A3). */
  private mesVencido(mes: number, pendientes: BscEjercicioKpi[]): boolean {
    return pendientes.some((k) => this.mesesPorKpi.get(k.idEjercicioKpi as number)?.get(mes)?.estado === "VENCIDO");
  }

  private hito(tipo: string, q: number, mes?: number): BscHito | null {
    return this.hitos.find((h) => h.tipo === tipo && h.trimestre === q && (mes == null || h.mes === mes)) ?? null;
  }

  /* ============================ Te toca ============================ */

  private armaTeToca(): void {
    this.teToca = [];
    this.mesTeToca = null;
    if (this.estado !== "DATOS") {
      return;
    }
    if (this.historica) {
      this.teToca = [{ texto: "La planeación se cargó de un FOR-011 histórico: es de consulta, no recibe avances." }];
      return;
    }
    if (this.cicloCerrado) {
      this.teToca = [{ texto: `${this.anio} está cerrado: es de consulta.` }];
      return;
    }

    const { mes, faltan, masVencidos } = this.mesPendiente();
    // El trimestre que se cierra: el primero en captura o regresado.
    const qCierre = TRIMESTRES.find((q) => {
      const e = this.trimestrePorQ.get(q)?.estatus;
      return e === "EN_CAPTURA" || e === "REGRESADO";
    }) ?? null;

    const s: Segmento[] = [{ texto: "Te toca: " }];
    if (mes != null) {
      this.mesTeToca = mes;
      s.push(...this.segmentosDelMes(mes, faltan, masVencidos));
    }
    if (qCierre != null) {
      s.push(...this.segmentosDelCierre(qCierre, mes != null));
    }
    if (mes == null && qCierre == null) {
      const enRevision = TRIMESTRES.find((q) => this.trimestrePorQ.get(q)?.estatus === "ENVIADO");
      const revision = enRevision ? ` Q${enRevision} está en revisión de Dirección.` : "";
      this.teToca = [{
        texto: `Estás al día: no hay meses pendientes de capturar.${revision}`,
      }];
      return;
    }
    s.push({ texto: "." });
    this.teToca = s;
  }

  /**
   * El mes de «Te toca»: el primero ya empezado, de un trimestre abierto, con KPIs vigentes sin capturar,
   * y cuántos le faltan. De los siguientes solo se cuentan los que ya vencieron (el mes en curso no urge).
   */
  private mesPendiente(): MesPendiente {
    let mes: number | null = null;
    let faltan = 0;
    let masVencidos = 0;
    for (let m = 1; m <= 12; m++) {
      if (!this.iniciado(m) || !this.abiertoACaptura(trimestreDeMes(m))) {
        continue;
      }
      const pendientes = this.faltanEnMes(m);
      if (pendientes.length === 0) {
        continue;
      }
      if (mes == null) {
        mes = m;
        faltan = pendientes.length;
      } else if (this.mesVencido(m, pendientes)) {
        masVencidos++;
      }
    }
    return { mes, faltan, masVencidos };
  }

  /** «capturar septiembre en 3 KPIs (venció el 5 oct; hay otro mes vencido)». */
  private segmentosDelMes(mes: number, faltan: number, masVencidos: number): Segmento[] {
    const s: Segmento[] = [];
    const kpis = `${faltan} ${faltan === 1 ? "KPI" : "KPIs"}`;
    if (this.viaExcel) {
      // El área captura con su Excel: el mes llega con la carga del FOR-011 (T1).
      s.push({ texto: "cargar " }, { texto: mesEnTexto(mes), fuerte: true }, { texto: ` con el FOR-011 (faltan ${kpis})` });
    } else {
      s.push({ texto: "capturar " }, { texto: mesEnTexto(mes), fuerte: true }, { texto: ` en ${kpis}` });
    }
    const h = this.hito("CAPTURA_MES", trimestreDeMes(mes), mes);
    const vencio = h?.estado === "VENCIDO" && h.fecha ? `venció el ${diaMes(h.fecha, this.anio)}` : "";
    const mas = otrosVencidos(masVencidos);
    if (vencio || mas) {
      s.push({ texto: ` (${[vencio, mas].filter(Boolean).join("; ")})` });
    }
    return s;
  }

  /** «y cerrar Q3 antes del 15 oct», «reenviar Q3 corregido, que venció el …» o «… (sin fecha límite)». */
  private segmentosDelCierre(qCierre: number, conMes: boolean): Segmento[] {
    const t = this.trimestrePorQ.get(qCierre);
    const h = this.hito("CIERRE_Q", qCierre);
    const s: Segmento[] = [
      { texto: conMes ? " y " : "" },
      { texto: t?.estatus === "REGRESADO" ? `reenviar Q${qCierre} corregido` : `cerrar Q${qCierre}` },
    ];
    if (h?.fecha && h.estado === "VENCIDO") {
      s.push({ texto: ", que venció el " }, { texto: diaMes(h.fecha, this.anio), fuerte: true });
    } else if (h?.fecha) {
      s.push({ texto: " antes del " }, { texto: diaMes(h.fecha, this.anio), fuerte: true });
    } else {
      s.push({ texto: " (sin fecha límite)" });
    }
    return s;
  }

  /* ============================ El ciclo del año ============================ */

  private pasoDePlaneacion(e: BscEjercicio): PasoCiclo {
    const estatus = ESTATUS_PLANEACION.find((x) => x.valor === e.estatus);
    const n = this.kpis.length;
    const importada = e.origen === "IMPORTACION_ARRANQUE" || e.origen === "IMPORTACION_HISTORICA";
    const partes = [
      importada && e.estatus === "AUTORIZADA" ? "Importada del FOR-011" : estatus?.texto ?? "Sin estatus",
      e.estatus === "AUTORIZADA" ? diaMes(e.fechaAutorizacion, this.anio, "") : "",
      `${n} ${n === 1 ? "KPI" : "KPIs"}`,
    ];
    return {
      q: null,
      numero: 1,
      titulo: `Planeación ${this.anio ?? ""}`.trim(),
      detalle: partes.filter(Boolean).join(" · "),
      clase: claseDePlaneacion(e.estatus),
      estadoTexto: estatus?.texto ?? "",
    };
  }

  private pasoDe(q: number): PasoCiclo {
    const t = this.trimestrePorQ.get(q);
    const f = this.fechasPorQ.get(q);
    const limite = f?.fechaLimiteEnvio ? `límite ${diaMes(f.fechaLimiteEnvio, this.anio)}` : "sin fecha límite";
    const base = { q, numero: q + 1, titulo: `Q${q}` };
    if (!t) {
      return { ...base, detalle: "Sin trimestre", clase: "is-espera", estadoTexto: "sin trimestre" };
    }
    let detalle = "";
    let clase = "is-espera";
    let estadoTexto = "";
    switch (t.estatus) {
      case "AUTORIZADO":
        detalle = [t.origen === "IMPORTACION" ? "Importado" : "Autorizado", pct(t.alcanceReal, ""),
          t.origen === "IMPORTACION" ? "" : diaMes(t.fechaResolucion, this.anio, "")].filter(Boolean).join(" · ");
        clase = "is-hecho";
        estadoTexto = "autorizado";
        break;
      case "ENVIADO":
        detalle = ["En revisión", pct(t.alcanceReal, ""), diaMes(t.fechaEnvio, this.anio, "")].filter(Boolean).join(" · ");
        clase = "is-enviado";
        estadoTexto = "enviado a revisión";
        break;
      case "EN_CAPTURA":
      case "REGRESADO": {
        const paso = this.pasoEnCaptura(t, q, limite);
        detalle = paso.detalle;
        clase = paso.clase;
        estadoTexto = paso.estadoTexto;
        break;
      }
      default:
        if (this.historica) {
          detalle = "Sin datos (histórico)";
        } else {
          const inicio = `${this.anio}-${String((q - 1) * 3 + 1).padStart(2, "0")}-01`;
          detalle = `Inicia ${diaMes(inicio, this.anio)} · ${limite}`;
        }
        estadoTexto = "pendiente";
    }
    if (t.sinEnviar) {
      detalle += " · vencido";
      clase = "is-alerta";
    }
    return { ...base, detalle, clase, estadoTexto };
  }

  /** Un trimestre en captura o regresado: cuántos de sus meses están completos y su fecha límite. */
  private pasoEnCaptura(
    t: BscEjercicioTrimestre,
    q: number,
    limite: string,
  ): Pick<PasoCiclo, "detalle" | "clase" | "estadoTexto"> {
    const completos = mesesDeTrimestre(q).filter((m) => this.vigentes(q).length > 0 && this.faltanEnMes(m).length === 0).length;
    return {
      detalle: `${t.estatus === "REGRESADO" ? "Regresado · " : ""}${completos} de 3 meses · ${limite}`,
      clase: t.estatus === "REGRESADO" ? "is-alerta" : "is-actual",
      estadoTexto: t.estatus === "REGRESADO" ? "regresado" : "en captura",
    };
  }

  /** La pestaña que se abre: la del mes de «Te toca», la abierta, la de hoy o la última. */
  private qInicial(): number {
    if (this.mesTeToca != null) {
      return trimestreDeMes(this.mesTeToca);
    }
    const abierta = TRIMESTRES.find((q) => {
      const e = this.trimestrePorQ.get(q)?.estatus;
      return e === "EN_CAPTURA" || e === "REGRESADO";
    });
    if (abierta != null) {
      return abierta;
    }
    const hoy = new Date();
    if (this.anio === hoy.getFullYear()) {
      return trimestreDeMes(hoy.getMonth() + 1);
    }
    return this.anio != null && this.anio < hoy.getFullYear() ? 4 : 1;
  }

  /** El mes que se abre en un trimestre: el de «Te toca», el primero ya empezado con pendientes, el último empezado o el primero. */
  private mesInicial(q: number): number {
    const meses = mesesDeTrimestre(q);
    if (this.mesTeToca != null && meses.includes(this.mesTeToca)) {
      return this.mesTeToca;
    }
    const iniciados = meses.filter((m) => this.iniciado(m));
    const conPendientes = iniciados.find((m) => this.faltanEnMes(m).length > 0);
    return conPendientes ?? iniciados.at(-1) ?? meses[0];
  }

  /* ============================ Pestañas y mes ============================ */

  eligeQ(q: number, enfocar = false): void {
    if (q === this.qElegido || !this.pasos.some((p) => p.q === q)) {
      return;
    }
    this.qElegido = q;
    this.mesElegido = this.mesInicial(q);
    this.armaTrimestre();
    this.cdr.detectChanges();
    if (enfocar) {
      this.host.nativeElement.querySelector<HTMLElement>(`#mi-bsc-pestana-${q}`)?.focus();
    }
  }

  /** Flechas, Inicio y Fin entre las pestañas (role="tablist"). */
  teclaPestana(evento: KeyboardEvent, q: number): void {
    const destinos: Record<string, number> = {
      ArrowRight: q === 4 ? 1 : q + 1,
      ArrowLeft: q === 1 ? 4 : q - 1,
      Home: 1,
      End: 4,
    };
    const destino = destinos[evento.key];
    if (destino != null) {
      evento.preventDefault();
      this.eligeQ(destino, true);
    }
  }

  eligeMes(mes: number): void {
    if (mes === this.mesElegido) {
      return;
    }
    this.mesElegido = mes;
    this.armaMes();
    this.cdr.detectChanges();
  }

  private armaTrimestre(): void {
    const q = this.qElegido;
    const vigentes = this.vigentes(q);
    this.opcionesMes = mesesDeTrimestre(q).map((mes) => {
      const capturados = vigentes.filter((k) => this.capturado(k.idEjercicioKpi as number, mes)).length;
      const vencido = vigentes.some((k) => this.mesesPorKpi.get(k.idEjercicioKpi as number)?.get(mes)?.estado === "VENCIDO");
      let clase = "";
      if (!this.abiertoACaptura(q)) {
        clase = "is-cerrado";
      } else if (vigentes.length > 0 && capturados === vigentes.length) {
        clase = "is-completo";
      } else if (vencido) {
        clase = "is-vencido";
      }
      return { mes, nombre: nombreMes(mes), capturados, vigentes: vigentes.length, clase };
    });
    const t = this.trimestrePorQ.get(q);
    this.regreso = "";
    if (t?.estatus === "REGRESADO") {
      const quien = [t.usuarioResuelve, diaMes(t.fechaResolucion, this.anio, "")].filter(Boolean).join(", ");
      const porQuien = quien ? ` (${quien})` : "";
      const motivo = t.motivo ? `: «${t.motivo}»` : "";
      this.regreso = `Dirección regresó Q${q}${porQuien}${motivo}.`
        + " Corrige lo que pidió y vuelve a enviarlo.";
    }
    this.cierre = this.armaCierre(q);
    this.fechas = this.armaFechas(q);
    this.armaMes();
  }

  private armaMes(): void {
    const q = this.qElegido;
    const mes = this.mesElegido;
    const vigentes = this.vigentes(q);
    const capturados = vigentes.filter((k) => this.capturado(k.idEjercicioKpi as number, mes)).length;
    this.tituloMes = `Captura de ${mesEnTexto(mes)} · ${capturados} de ${vigentes.length} ${vigentes.length === 1 ? "KPI" : "KPIs"}`;
    this.avanceMes = vigentes.length > 0 ? Math.round((capturados / vigentes.length) * 100) : 0;
    const h = this.hito("CAPTURA_MES", q, mes);
    const dia = this.ciclo?.diaLimiteCapturaMensual;
    const limite = h?.fecha ?? (dia == null ? null : fechaLimiteDelMes(this.anio, mes, dia));
    this.fechaLimiteMes = limite ? diaMes(limite, this.anio) : "";
    this.limiteMes = limite ? `límite ${this.fechaLimiteMes}` : "sin fecha límite";
    this.notaMes = this.notaDeCaptura(q);
    const abierto = this.capturaHabilitada && this.abiertoACaptura(q);
    this.renglones = this.kpis.map((k) => this.renglon(k, q, mes, abierto));
  }

  private notaDeCaptura(q: number): string {
    const t = this.trimestrePorQ.get(q);
    if (this.historica) {
      return "Planeación cargada de un FOR-011 histórico: es de consulta.";
    }
    if (this.cicloCerrado) {
      return `${this.anio} está cerrado: ya no se capturan avances.`;
    }
    if (this.viaExcel) {
      return "El área captura con su Excel FOR-011: los avances se cargan en Cargar Excel.";
    }
    if (!this.puedeCapturar) {
      return "Tu perfil no captura en esta área.";
    }
    if (!t) {
      return `Q${q} no tiene trimestre en la planeación.`;
    }
    if (t.estatus === "ENVIADO") {
      return `Q${q} ya se envió a revisión: sus meses ya no se capturan (si hay que corregir, Dirección lo regresa).`;
    }
    if (t.estatus === "AUTORIZADO") {
      return `Q${q} ya está autorizado: sus meses ya no se capturan.`;
    }
    return "";
  }

  private renglon(k: BscEjercicioKpi, q: number, mes: number, abierto: boolean): RenglonKpi {
    const id = k.idEjercicioKpi as number;
    const m = this.mesesPorKpi.get(id)?.get(mes);
    const estado = ESTADO_MES.find((e) => e.valor === m?.estado) ?? null;
    const capturado = m?.id != null;
    const vigente = aplicaEn(k, q);
    const detalle = this.detalleDe(k, q, mes, m);
    let avance = "";
    if (vigente) {
      const c = this.capturasPorKpi.get(id)?.get(q);
      const real = numero(c?.avanceReal);
      avance = real == null
        ? `Avance Q${q} sin capturar`
        : `Avance Q${q}: ${pct(real)} · acumulado ${pct(c?.acumReal ?? acumRealEn(k, q))} de ${pct(c?.acumProg ?? acumProgEn(k, q))}`;
    }
    return {
      idEjercicioKpi: id,
      objetivo: (k.objetivo ?? "").trim() || "KPI sin objetivo",
      estado,
      capturado,
      vigente,
      detalle,
      avance,
      capturable: abierto && m?.estado !== "BLOQUEADO",
    };
  }

  /** Lo que dice el renglón del KPI según el estado de su mes ("Capturado 26 sep por Ana López"…). */
  private detalleDe(k: BscEjercicioKpi, q: number, mes: number, m: BscKpiMes | undefined): string {
    const capturado = m?.id != null;
    const fecha = diaMes(m?.fechaModificacion || m?.fechaCreacion, this.anio, "");
    const quien = (m?.usuarioAlta ?? "").trim();
    const enFecha = fecha ? ` ${fecha}` : "";
    const porQuien = quien ? ` por ${quien}` : "";
    switch (m?.estado) {
      case "BLOQUEADO":
        return capturado
          ? `Cerrado · capturado${enFecha}${porQuien}`
          : "Cerrado sin captura";
      case "CAPTURADO":
        return `Capturado${enFecha}${porQuien}`;
      case "NO_APLICA":
        return (k.trimestreInicio ?? 1) > q
          ? `Aplica desde Q${k.trimestreInicio} · solo registra actividad`
          : `NA en Q${q} · solo registra actividad`;
      case "VENCIDO": {
        const vencio = this.fechaLimiteMes ? ` · venció el ${this.fechaLimiteMes}` : " · vencido";
        return `Sin capturar${vencio}`;
      }
      default:
        return this.iniciado(mes) ? "Pendiente" : "Pendiente · el mes aún no empieza";
    }
  }

  private armaCierre(q: number): CierreQ | null {
    const t = this.trimestrePorQ.get(q);
    if (!t) {
      return null;
    }
    const exigeMeses = this.ciclo?.exigirMesesParaCerrar !== false;
    const faltan = exigeMeses
      ? this.vigentes(q).filter((k) => mesesDeTrimestre(q).some((m) => !this.capturado(k.idEjercicioKpi as number, m))).length
      : 0;
    const cuando = (texto: string | undefined) => diaMes(texto, this.anio, "");
    switch (t.estatus) {
      case "EN_CAPTURA":
      case "REGRESADO":
        return this.cierreEnCaptura(q, faltan, exigeMeses);
      case "ENVIADO": {
        const elDia = t.fechaEnvio ? ` el ${cuando(t.fechaEnvio)}` : "";
        const quien = t.usuarioEnvia ? ` (${t.usuarioEnvia})` : "";
        return {
          boton: false,
          habilitado: false,
          textoBoton: "",
          texto: `Q${q} se envió a revisión${elDia}${quien}: Dirección lo autoriza o lo regresa.`,
        };
      }
      case "AUTORIZADO": {
        const elDia = t.fechaResolucion ? ` el ${cuando(t.fechaResolucion)}` : "";
        return {
          boton: false,
          habilitado: false,
          textoBoton: "",
          texto: t.origen === "IMPORTACION"
            ? `Q${q} se importó autorizado del FOR-011: alcance ${pct(t.alcanceReal)} de ${pct(t.alcanceProgramado)} programado.`
            : `Q${q} autorizado${elDia}: alcance ${pct(t.alcanceReal)} de ${pct(t.alcanceProgramado)} programado.`,
        };
      }
      default:
        return {
          boton: false,
          habilitado: false,
          textoBoton: "",
          texto: this.historica
            ? "Sin datos: el histórico se cargó hasta un trimestre anterior."
            : `Q${q} se abre a cierre cuando Dirección autorice Q${q - 1}; sus meses ya se pueden capturar.`,
        };
    }
  }

  /** El pie de un trimestre en captura o regresado: «Cerrar Qn» y, si no se puede, por qué. */
  private cierreEnCaptura(q: number, faltan: number, exigeMeses: boolean): CierreQ {
    const anterior = q === 1 ? null : this.trimestrePorQ.get(q - 1);
    const anteriorOk = q === 1 || anterior?.estatus === "AUTORIZADO";
    let texto: string;
    if (this.cicloCerrado) {
      texto = `${this.anio} está cerrado.`;
    } else if (!anteriorOk) {
      texto = `Q${q - 1} todavía no está autorizado: Q${q} se envía después.`;
    } else if (faltan > 0) {
      texto = `Al completar los meses de Q${q} se habilita el cierre.`;
    } else {
      texto = exigeMeses
        ? `Los meses de Q${q} están completos: en el cierre se revisa el avance real de cada KPI y se envía a Dirección.`
        : `En el cierre se revisa el avance real de cada KPI y se envía a Dirección (${this.anio} no exige los tres meses).`;
    }
    return {
      boton: this.puedeCerrar,
      habilitado: this.puedeCerrar && anteriorOk && faltan === 0 && !this.cicloCerrado && !this.historica,
      textoBoton: faltan > 0 ? `Cerrar Q${q} (faltan ${faltan})` : `Cerrar Q${q}`,
      texto,
    };
  }

  /** Las fechas del trimestre: la captura de sus 3 meses y las cuatro de T4, con el estado de su hito. */
  private armaFechas(q: number): FechaQ[] {
    const f = this.fechasPorQ.get(q);
    const dia = this.ciclo?.diaLimiteCapturaMensual;
    const filas: FechaQ[] = mesesDeTrimestre(q).map((mes) =>
      this.fecha(
        `mes-${mes}`,
        `Captura de ${mesEnTexto(mes)}`,
        dia == null ? null : fechaLimiteDelMes(this.anio, mes, dia),
        this.hito("CAPTURA_MES", q, mes),
        "Sin fecha límite",
      ),
    );
    filas.push(
      this.fecha("captura", `Cierre de captura de Q${q}`, f?.fechaCierreCaptura ?? null, this.hito("CAPTURA_Q", q), "Sin fecha límite"),
      this.fecha("envio", `Cierre y envío de Q${q}`, f?.fechaLimiteEnvio ?? null, this.hito("CIERRE_Q", q), "Sin fecha límite"),
      this.fecha("revision", "Revisión de Dirección", f?.fechaFinRevision ?? null, this.hito("REVISION_Q", q), "Sin fecha límite"),
      this.fecha("presentacion", "Presentación", f?.fechaPresentacion ?? null, this.hito("PRESENTACION_Q", q), "Sin fecha"),
    );
    const anticipacion = this.ciclo?.anticipacionRecordatorioDias;
    const dias = anticipacion === 1 ? "día" : "días";
    this.notaFechas = anticipacion != null && anticipacion > 0
      ? `Las fechas las define Calidad por año en Parámetros. Recordatorio en las notificaciones del COS ${anticipacion} ${dias} antes de cada fecha.`
      : "Las fechas las define Calidad por año en Parámetros.";
    return filas;
  }

  private fecha(clave: string, texto: string, fecha: string | null, h: BscHito | null, sinFecha: string): FechaQ {
    if (!fecha) {
      return { clave, texto, fecha: sinFecha, estado: "", clase: "is-sin-fecha", icono: "" };
    }
    let estado = "";
    let clase = "";
    let icono = "";
    switch (h?.estado) {
      case "CUMPLIDO":
        estado = "Cumplido";
        clase = "is-ok";
        icono = "check_circle";
        break;
      case "VENCIDO":
        estado = "Vencido";
        clase = "is-vencido";
        icono = "event_busy";
        break;
      case "EN_ESPERA":
        estado = "En espera";
        icono = "hourglass_empty";
        break;
      case "PENDIENTE":
        if (h.recordar) {
          estado = "Próximo";
          clase = "is-proximo";
          icono = "notifications_active";
        }
        break;
    }
    return { clave, texto, fecha: diaMes(fecha, this.anio), estado, clase, icono };
  }

  /* ============================ Capturar ============================ */

  /** El botón del hero: el primer KPI pendiente del mes de «Te toca», encadenando los demás. */
  capturarTeToca(): void {
    const mes = this.mesTeToca;
    if (mes == null || !this.capturaHabilitada) {
      return;
    }
    const q = trimestreDeMes(mes);
    if (q !== this.qElegido) {
      this.qElegido = q;
      this.mesElegido = mes;
      this.armaTrimestre();
    } else if (mes !== this.mesElegido) {
      this.mesElegido = mes;
      this.armaMes();
    }
    this.cdr.detectChanges();
    const primero = this.faltanEnMes(mes)[0];
    if (primero?.idEjercicioKpi != null) {
      this.abrirCaptura(primero.idEjercicioKpi, mes, new Set());
    }
  }

  capturar(r: RenglonKpi): void {
    if (!r.capturable) {
      return;
    }
    this.abrirCaptura(r.idEjercicioKpi, this.mesElegido, new Set());
  }

  /**
   * Abre el cuadro de captura. Con «Guardar y seguir» se abre el siguiente
   * KPI vigente sin capturar del mismo mes (después de volver a consultar),
   * sin repetir los que ya se abrieron en la cadena.
   */
  private abrirCaptura(idEjercicioKpi: number, mes: number, visitados: Set<number>): void {
    const k = this.kpiPorId.get(idEjercicioKpi);
    if (!k || this.dialogo || this.destruido) {
      return;
    }
    const q = trimestreDeMes(mes);
    const siguientes = this.pendientesDe(mes).filter((id) => id !== idEjercicioKpi && !visitados.has(id));
    this.mensajeGuardado = "";
    this.dialogo = abrirCapturaMesBsc(this.dialog, {
      kpi: k,
      anio: this.anio,
      area: this.nombreArea,
      mes,
      mesGuardado: this.mesesPorKpi.get(idEjercicioKpi)?.get(mes) ?? null,
      avance: this.capturasPorKpi.get(idEjercicioKpi)?.get(q) ?? null,
      estatusTrimestre: this.trimestrePorQ.get(q)?.estatus ?? null,
      haySiguiente: siguientes.length > 0,
    });
    this.dialogo.afterClosed().subscribe((r) => {
      this.dialogo = undefined;
      if (!r || this.destruido) {
        this.enfocaRenglon(idEjercicioKpi);
        return;
      }
      this.mensajeGuardado = r.mensaje;
      if (!r.guardado && !r.recargar) {
        this.cdr.detectChanges();
        return;
      }
      const cadena = new Set(visitados);
      cadena.add(idEjercicioKpi);
      this.consultar(true, () => {
        if (!r.siguiente) {
          this.enfocaRenglon(idEjercicioKpi);
          return;
        }
        const sigue = this.pendientesDe(mes).find((id) => !cadena.has(id));
        if (sigue != null) {
          this.abrirCaptura(sigue, mes, cadena);
        }
      });
    });
  }

  /** Los KPI vigentes del mes sin capturar, si su trimestre recibe captura. */
  private pendientesDe(mes: number): number[] {
    if (!this.capturaHabilitada || !this.abiertoACaptura(trimestreDeMes(mes))) {
      return [];
    }
    return this.faltanEnMes(mes).map((k) => k.idEjercicioKpi as number);
  }

  private enfocaRenglon(idEjercicioKpi: number): void {
    setTimeout(() =>
      this.host.nativeElement.querySelector<HTMLElement>(`[data-kpi="${idEjercicioKpi}"] .mi-bsc__accion`)?.focus(),
    );
  }

  /* ============================ trackBy ============================ */

  trackPaso(_: number, p: PasoCiclo): number {
    return p.q ?? 0;
  }

  trackMes(_: number, m: OpcionMes): number {
    return m.mes;
  }

  trackRenglon(_: number, r: RenglonKpi): number {
    return r.idEjercicioKpi;
  }

  trackFecha(_: number, f: FechaQ): string {
    return f.clave;
  }

  trackMovimiento(_: number, m: Movimiento): string {
    return m.clave;
  }

  trackSegmento(i: number): number {
    return i;
  }

  /** "septiembre" (en minúscula, para el botón y las etiquetas). */
  textoMes(mes: number | null): string {
    return mesEnTexto(mes);
  }

  private mensaje(res: BscRes | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || porOmision;
  }
}

/* ============================ Funciones puras ============================ */

function anioValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 1900 && n < 3000 ? n : null;
}

function idValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * La fecha límite de la captura de un mes (la del VENCIDO de A3 y del hito
 * CAPTURA_MES): fin de mes + día límite del ciclo, en `yyyy-MM-dd`.
 */
function fechaLimiteDelMes(anio: number | null, mes: number, dia: number): string {
  const a = anio ?? new Date().getFullYear();
  const fin = new Date(a, mes, 0);
  fin.setDate(fin.getDate() + dia);
  return `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, "0")}-${String(fin.getDate()).padStart(2, "0")}`;
}

/** Un renglón de la bitácora: lo que pasó, quién y cuándo (lo más reciente primero). */
function movimiento(h: BscHistorial, i: number): Movimiento {
  const accion = (h.accion && ACCION_HISTORIAL[h.accion]) || h.accion || SIN_DATO;
  const partes = h.accion && COMENTARIO_DEL_SISTEMA.has(h.accion) ? partesDelSistema(h) : partesDePersona(h);
  const id = h.id ?? `i${i}`;
  return {
    clave: `${id}`,
    cuando: fechaHora(h.fecha),
    accion,
    quien: (h.usuario ?? "").trim() || (h.idUsuario != null ? `Usuario ${h.idUsuario}` : SIN_DATO),
    detalle: partes.join(" · "),
  };
}

/** "Capturó septiembre de «KPI»." y, del avance, "Real 90% · programado 100%": ya dicen el mes o el Q. */
function partesDelSistema(h: BscHistorial): string[] {
  const partes: string[] = [];
  if (h.comentario) {
    partes.push(h.comentario);
  } else if (h.trimestre) {
    partes.push(trimestreYMes(h.trimestre, h.mes));
  }
  if (h.valorNuevo) {
    partes.push(h.valorNuevo);
  }
  return partes;
}

/** Lo que hizo una persona: el trimestre, el cambio de valor y su comentario entre comillas. */
function partesDePersona(h: BscHistorial): string[] {
  const partes: string[] = [];
  if (h.trimestre) {
    partes.push(trimestreYMes(h.trimestre, h.mes));
  }
  if (h.valorAnterior || h.valorNuevo) {
    partes.push(h.valorAnterior && h.valorNuevo ? `${h.valorAnterior} → ${h.valorNuevo}` : (h.valorNuevo ?? h.valorAnterior ?? ""));
  }
  if (h.comentario) {
    partes.push(`«${h.comentario}»`);
  }
  return partes;
}

/** "Q3 · septiembre", o "Q3" sin mes. */
function trimestreYMes(trimestre: number, mes: number | undefined): string {
  return mes ? `Q${trimestre} · ${mesEnTexto(mes)}` : `Q${trimestre}`;
}

/** Las capturas del avance por KPI y por trimestre (las que no traen los dos no entran). */
function indiceDeCapturas(capturas: BscKpiTrimestre[]): Map<number, Map<number, BscKpiTrimestre>> {
  const indice = new Map<number, Map<number, BscKpiTrimestre>>();
  for (const c of capturas) {
    if (c.idEjercicioKpi == null || c.trimestre == null) {
      continue;
    }
    if (!indice.has(c.idEjercicioKpi)) {
      indice.set(c.idEjercicioKpi, new Map());
    }
    indice.get(c.idEjercicioKpi)?.set(c.trimestre, c);
  }
  return indice;
}

/** "hay otro mes vencido" o "hay otros N meses vencidos"; vacío sin otros. */
function otrosVencidos(n: number): string {
  if (n === 0) {
    return "";
  }
  return n === 1 ? "hay otro mes vencido" : `hay otros ${n} meses vencidos`;
}

/** El paso de la planeación: hecho si está autorizada, alerta si se regresó, actual si no. */
function claseDePlaneacion(estatus: BscEjercicio["estatus"]): string {
  if (estatus === "AUTORIZADA") {
    return "is-hecho";
  }
  return estatus === "REGRESADA" ? "is-alerta" : "is-actual";
}
